import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Choices, KIND_WORD, LabelPics } from "./printLabelsParts";
import { ScanCheck } from "./ScanCheck";
import { useSheetPrinter } from "./useSheetPrinter";

const levelWord = (l) => (l === "0" ? "Floor (0)" : `Level ${l}`);
const NOT_HOLDER = "pallet racks only";

// ONE "PRINT LABELS" FOR EVERY RACK, BAY, HOLDER AND GLASS RACK (his call, 22 Oct): what → how much → preview → print
export function PrintLabelsSheet({ target, onClose, onPrint }) {
  const { node, kind, bay: startBay, scope: startScope, level: startLevel, slot_id: startSlot } = target;
  const rack = kind === "rack";
  const [what, setWhat] = useState(rack ? "both" : "scan");
  const [scope, setScope] = useState(startScope || (rack ? (startBay ? "bay" : "rack") : "all"));
  const [bay, setBay] = useState(startBay || null);
  const [level, setLevel] = useState(startLevel || null);
  const [slotId, setSlotId] = useState(startSlot || null);
  const [slotSize, setSlotSize] = useState(null);
  const [data, setData] = useState(null);
  const [lists, setLists] = useState(null);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const lastOne = useRef(null);
  const { printSheet: printSingle, printDialogs } = useSheetPrinter(() => setCheck(lastOne.current));
  const printOne = (item) => {
    lastOne.current = item; setCheck(null);
    printSingle({ sizes: [item.size], items: [item] }, `${item.code} · one ${KIND_WORD[item.kind].toLowerCase()} label`);
  };

  const body = useMemo(() => (rack
    ? { loc_id: node.id, what, bay: scope === "rack" ? null : bay, level: scope === "level" ? level : null }
    : { loc_id: node.id, what: "scan", part: scope, slot_id: scope === "slot" ? slotId : null, slot_size: slotSize }), [rack, node.id, what, scope, bay, level, slotId, slotSize]);
  const ready = rack ? (scope !== "bay" || bay) && (scope !== "level" || level) : (scope !== "slot" || slotId);
  const key = JSON.stringify(body);

  useEffect(() => {
    if (!ready) { setData(null); return undefined; }
    let live = true;
    setBusy(true);
    api.post("/places/labels", JSON.parse(key)).then(({ data: d }) => {
      if (!live) return;
      setData(d);
      setLists((l) => l || d);
    }).catch((e) => live && toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't draw the labels.")).finally(() => live && setBusy(false));
    return () => { live = false; };
  }, [key, ready]);

  const bays = lists?.bays || [];
  const pickScope = (s) => { setScope(s); if (s === "level") setBay(null); if (s === "bay" && !bay && bays.length) setBay(bays[0]); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="print-labels-sheet" className="rounded-xl sm:max-w-3xl max-h-kb overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Print labels — {node.name} <span className="font-mono text-sm">{node.short_code}</span></DialogTitle>
          <DialogDescription>Pick what, pick how much, check every label, print.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Choices id="print-labels-what" title="1 · What" value={what} onChange={setWhat}
            options={[["scan", "Scan labels"], ["shelf", "Shelf labels", !rack && NOT_HOLDER], ["both", "Both", !rack && NOT_HOLDER]]} />
          {rack ? (
            <RackScope scope={scope} onScope={pickScope} bays={bays} bay={bay} onBay={setBay} levels={lists?.levels || []} level={level} onLevel={setLevel} />
          ) : (
            <HolderScope scope={scope} onScope={setScope} slots={lists?.slots || []} slotId={slotId} onSlot={setSlotId}
              size={slotSize || lists?.slot_size || "4x3"} onSize={setSlotSize} />
          )}
          {check && <ScanCheck item={check} onClose={() => setCheck(null)} />}
          {ready ? <LabelPics data={data} loading={busy} onPrintOne={printOne} /> : <p className="text-sm text-slate-500" data-testid="print-labels-pick-first">Pick one above to see the labels.</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
          <Button variant="outline" data-testid="print-labels-close" onClick={onClose} className="rounded-lg h-11">Close</Button>
          <Button data-testid="print-labels-go" disabled={!ready || busy || !data?.count} onClick={() => onPrint(data, `${node.short_code} · ${what === "both" ? "shelf + scan" : what} labels`)}
            className="rounded-lg h-11 bg-[#1F1F1F] hover:bg-black text-white font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-1.5" />4 · Print {ready && data ? data.count : ""}</>}
          </Button>
        </div>
        {printDialogs}
      </DialogContent>
    </Dialog>
  );
}

function RackScope({ scope, onScope, bays, bay, onBay, levels, level, onLevel }) {
  return (
    <div className="space-y-3">
      <Choices id="print-labels-scope" title="2 · How much" value={scope} onChange={onScope}
        options={[["level", "One level"], ["bay", "One bay"], ["rack", "Whole rack"]]} />
      {scope === "level" && (
        <>
          <Choices id="print-labels-level" title="Which level" value={level} onChange={onLevel} options={levels.map((l) => [l, levelWord(l)])} />
          <Choices id="print-labels-inbay" title="In" value={bay || "every"} onChange={(v) => onBay(v === "every" ? null : v)}
            options={[["every", "Every bay"], ...bays.map((b) => [b, `Bay ${b}`])]} />
        </>
      )}
      {scope === "bay" && <Choices id="print-labels-bay" title="Which bay" value={bay} onChange={onBay} options={bays.map((b) => [b, `Bay ${b}`])} />}
    </div>
  );
}

function HolderScope({ scope, onScope, slots, slotId, onSlot, size, onSize }) {
  return (
    <div className="space-y-3">
      <Choices id="print-labels-scope" title="2 · How much" value={scope} onChange={onScope}
        options={[["all", slots.length ? `Holder + its ${slots.length} slots` : "Holder + its slots"], ["holder", "Just the holder"], ["slot", "One slot", !slots.length && "nothing inside yet"]]} />
      {scope === "slot" && <Choices id="print-labels-slot" title="Which slot" value={slotId} onChange={onSlot} options={slots.map((s) => [s.id, s.code.split("-").pop()])} />}
      {scope !== "holder" && slots.length > 0 && (
        <Choices id="print-labels-size" title="Slot labels" value={size} onChange={onSize}
          options={[["4x3", "4×3 — same roll as the holder"], ["3x1", "3×1 — small, the roll changes once"]]} />
      )}
    </div>
  );
}
